import React, { useMemo } from 'react';
import { Stack, Text, Group, ScrollArea, Box } from '@mantine/core';
import { CheckCircle, Plus, Edit, Share, UserPlus, Activity } from 'lucide-react';
import { getCompactRowStyle } from './compactRowStyles';
import { useDashboardData } from '../../../../hooks/dashboard/useDashboardData';
import { formatRelativeTime } from '../../../../utils/dashboard/dateHelpers';

interface RecentActivityProps { 
  className?: string;
  onEditTodo?: (todoId: number) => void;
}

interface ActivityItem {
  id: string;
  type: 'task_completed' | 'task_created' | 'task_assigned' | 'list_shared' | 'note_created';
  title: string;
  description: string;
  timestamp: Date;
  icon: React.ReactNode;
  color: string;
  taskId?: number; // Add taskId for task-related activities
}

export const RecentActivity: React.FC<RecentActivityProps> = ({ className, onEditTodo }) => {
  const dashboardData = useDashboardData();

  const activityFeed = useMemo(() => {
    const activities: ActivityItem[] = [];

    dashboardData.recentlyCompletedTasks.slice(0, 8).forEach(task => {
      activities.push({
        id: `completed-${task.id}`,
        type: 'task_completed',
        title: task.title,
        description: `Completed in ${task.listName}`,
        timestamp: new Date(task.completedAt || task.createdAt),
        icon: <CheckCircle size={16} />,
        color: 'green',
        taskId: task.id
      });
    });

    dashboardData.recentlyCreatedTasks.slice(0, 5).forEach(task => {
      activities.push({
        id: `created-${task.id}`,
        type: 'task_created',
        title: task.title,
        description: `Created in ${task.listName}`,
        timestamp: new Date(task.createdAt),
        icon: <Plus size={16} />,
        color: 'blue',
        taskId: task.id
      });
    });

    dashboardData.todos
      .filter(t => t.assignedTo && new Date(t.createdAt) > new Date(Date.now() - 7 * 86400000))
      .slice(0, 3)
      .forEach(task => {
        activities.push({
          id: `assigned-${task.id}`,
          type: 'task_assigned',
          title: task.title,
            description: `Assigned to ${task.assignedTo}`,
          timestamp: new Date(task.createdAt),
          icon: <UserPlus size={16} />,
          color: 'purple',
          taskId: task.id
        });
      });

    dashboardData.notes
      .sort((a,b)=> new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0,3)
      .forEach(note => {
        activities.push({
          id: `note-${note.id}`,
          type: 'note_created',
          title: note.title || 'Untitled Note',
          description: 'Note updated',
          timestamp: new Date(note.updatedAt),
          icon: <Edit size={16} />,
          color: 'orange'
        });
      });

    dashboardData.todoLists
      .filter(list => list.isShared && list.sharedWith.length > 0)
      .slice(0,2)
      .forEach(list => {
        activities.push({
          id: `shared-${list.id}`,
          type: 'list_shared',
          title: list.name,
          description: `Shared with ${list.sharedWith.length}`,
          timestamp: new Date(list.createdAt),
          icon: <Share size={16} />,
          color: 'cyan'
        });
      });

    return activities
      .sort((a,b)=> b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0,15);
  }, [dashboardData]);

  const getActivityIcon = (item: ActivityItem) => (
    <div style={{
      width:22,
      height:22,
      borderRadius:'50%',
      display:'flex',
      alignItems:'center',
      justifyContent:'center',
      background:`var(--mantine-color-${item.color}-1)`,
      border:`1px solid var(--mantine-color-${item.color}-3)`,
      color:`var(--mantine-color-${item.color}-7)`
    }}>
      {item.icon}
    </div>
  );

  if (activityFeed.length === 0) {
    return (
      <Stack gap="xs" className={className}>
        <Group gap={6}>
          <Activity size={16} style={{ color: 'var(--mantine-color-blue-6)' }} />
          <Text size="sm" fw={600}>Activity</Text>
        </Group>
        <Text size="xs" c="dimmed">No recent activity</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="xs" className={className}>
      <Group gap={6}>
        <Activity size={16} style={{ color: 'var(--mantine-color-blue-6)' }} />
        <Text size="sm" fw={600}>Activity</Text>
        <Text size="xs" c="dimmed">{activityFeed.length}</Text>
      </Group>
  <ScrollArea h={210} offsetScrollbars>
        <Stack gap={4}>
          {activityFeed.slice(0,10).map((activity, index) => (
            <Group
              key={activity.id}
              gap={6}
              wrap="nowrap"
              className="mini-row activity-row"
              data-dark-style
              style={{
                ...getCompactRowStyle(index < 3 ? 'blue':'gray'),
                border: 'none',
                // Darker tint for better readability
                background: `var(--mantine-color-${index < 3 ? 'blue':'gray'}-2)`,
                color: 'var(--mantine-color-dark-9)',
                // Provide dark mode background variable for global override selector
                ['--row-bg' as any]: 'var(--mantine-color-dark-6)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                cursor: activity.taskId ? 'pointer' : 'default'
              }}
              onClick={() => activity.taskId && onEditTodo?.(activity.taskId)}
            >
              {getActivityIcon(activity)}
              <Box style={{ flex:1, minWidth:0 }}>
                <Text size="xs" fw={500} lineClamp={1}>{activity.title}</Text>
                <Group gap={6} wrap="nowrap">
                  <Text size="xs" c="dimmed" lineClamp={1}>{activity.description}</Text>
                  <Text size="xs" c="dimmed" style={{ whiteSpace:'nowrap' }}>{formatRelativeTime(activity.timestamp)}</Text>
                </Group>
              </Box>
            </Group>
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};